import { differenceInMinutes, parse } from 'date-fns';
import { z } from 'zod';

import { type MenuCategory, isPrimaryCategory } from '@lib/entities/Menu';
import { type DeliveryType } from '@lib/entities/Store';
import { Point } from '@lib/types/location';
import { $b, $bareDate, $n, $s } from '@lib/utils/schema';
import { formatPhone, pluralize } from '@lib/utils/strings';

export interface Order {
    address_1: string;
    carbon_offset_usd?: number;
    cc_masked?: string;
    delivery_address_1?: string;
    delivery_address_2?: string;
    delivery_address_3?: string;
    delivery_address_lat?: number | string; // who knows what these will be
    delivery_address_lng?: number | string;
    delivery_fee: number;
    delivery_type: string;
    deliveryaddress_id: number;
    discount: number;
    isZeroWaste: number;
    items: OrderItem[];
    open: string;
    order_id: number;
    order_pickup_timestamp?: string;
    order_timestamp: string;
    payment_details?: string;
    payment_method: string;
    placed_by: number;
    /**
     * "Salad bucks" + reward credit (decimal)
     */
    reward_applied_usd: number;
    scheduled: boolean;
    secondary_tax: number;
    service_fee: number;
    soda_tax: number;
    store_id: number;
    store_lat: number;
    store_lng: number;
    store_name: string;
    subtotal: number;
    tax: number;
    tip: number;
    total: number;
    user_id: number;
    utensil_discount?: number;
    wait_time: string;
    /**
     * User information (used for guest checkout)
     */
    firstName?: string;
    lastName?: string;
    email?: string;
    phone: string;
    delivery_address_name?: string;
    gift_card_amount_applied?: number; // Optional, only if gift card is applied
    extra_fee: number;
    extra_fee_label?: string;
    /**
     * Billing location captured at checkout for the Rokt confirmation placement (JSAD-6636).
     * Client-only: never returned by the API, so absent on orders loaded from order history.
     */
    billingZip?: string;
    billingState?: string;
}

export interface DeliveryOrder extends Order {
    // hahahaha
    delivery_address_lat: number | string;
    delivery_address_lng: number | string;
}

export interface OrderItem {
    id: number;
    menuitem_id: number;
    menu_item_name: string;
    category: string;
    image_url: string;
    item_type: number;
    subtype: number;
    active: string;
    customizable: string;
    quantity: number;
    for_whom?: string;
    chopping_pref: string;
    dressing_pref: string;
    dressing_amount?: string;
    in_a_wrap?: string;
    bread: string;
    request: string;
    ingredients?: string;
    item_subtotal: number;
    reuse: boolean;
    customizations?: OrderItemCustomization[];
    is_pick_two?: boolean;
    items?: OrderItem[];
    saladai_llm_out_id?: string;
}

export interface OrderItemCustomization {
    id: number;
    ingredient_id: number;
    quantity: number;
    order_menuitem_id: number;
    name: string;
}

export const $OrderItemCustomization = z.object({
    id: $n,
    ingredient_id: $n,
    quantity: $n,
    order_menuitem_id: $n,
    name: $s
});

const $baseOrderItem = z.object({
    id: $n,
    menuitem_id: $n,
    menu_item_name: $s,
    category: $s,
    image_url: $s,
    item_type: $n,
    subtype: $n,
    active: $s,
    customizable: $s,
    quantity: $n,
    chopping_pref: $s,
    dressing_pref: $s,
    dressing_amount: $s.optional(),
    in_a_wrap: $s.optional(),
    bread: $s,
    request: $s,
    ingredients: $s.optional(),
    item_subtotal: $n,
    reuse: $b,
    customizations: z.array($OrderItemCustomization).optional(),
    is_pick_two: $b.optional()
});

// type decl necessary here, see: https://github.com/colinhacks/zod#recursive-types
export const $OrderItem: z.ZodType<OrderItem> = $baseOrderItem.extend({
    items: z.lazy(() => z.array($OrderItem).optional())
});

export const $Order = z.object({
    address_1: $s,
    carbon_offset_usd: $n.optional(),
    delivery_address_1: $s.optional(),
    delivery_address_2: $s.optional(),
    delivery_address_3: $s.optional(),
    delivery_address_lat: z.union([$n, $s]).optional(),
    delivery_address_lng: z.union([$n, $s]).optional(),
    delivery_fee: $n,
    delivery_type: $s,
    deliveryaddress_id: $n,
    discount: $n,
    isZeroWaste: $n,
    items: z.array($OrderItem),
    open: $s,
    order_id: $n,
    order_pickup_timestamp: $s.optional(),
    order_timestamp: $s,
    payment_details: $s.optional(),
    payment_method: $s,
    placed_by: $n,
    reward_applied_usd: $n,
    scheduled: $b,
    secondary_tax: $n,
    service_fee: $n,
    soda_tax: $n,
    store_id: $n,
    store_lat: $n,
    store_lng: $n,
    store_name: $s,
    subtotal: $n,
    tax: $n,
    tip: $n,
    total: $n,
    user_id: $n,
    utensil_discount: $n.optional(),
    wait_time: $s,
    firstName: $s.optional(),
    lastName: $s.optional(),
    email: $s.optional(),
    phone: $s,
    delivery_address_name: $s.optional(),
    gift_card_amount_applied: $n.optional(),
    extra_fee: $n,
    extra_fee_label: $s.optional(),
    // Client-only (see the interface above). Declared so a future refactor that starts parsing
    // enriched orders doesn't silently strip them and regress JSAD-6636.
    billingZip: $s.optional(),
    billingState: $s.optional()
});

export const $RemoteOrderStatus = z.object({
    timestamp: $bareDate,
    status: z.enum(['accepted', 'fulfilled', 'cancelled', 'Cancelled', 'delivered'])
    // Cancelled is added because of a typo in the backend when the order is cancelled from Admin 2.0
});

export type RemoteOrderStatus = z.infer<typeof $RemoteOrderStatus>;

export type OrderState = RemoteOrderStatus['status'] | 'unknown';

export interface OrderStatus {
    orderId: number;
    deliveryType: DeliveryType;
    lastUpdatedAt: Date;
    state: OrderState;
}

export const orderStatus = (order: Order, remote?: RemoteOrderStatus): OrderStatus => {
    return {
        orderId: order.order_id,
        deliveryType: isPickup(order) ? 'Pickup' : 'Delivery',
        lastUpdatedAt: remote ? new Date(remote.timestamp) : new Date(),
        state: remote ? remote.status : 'unknown'
    };
};

// TODO: this hardcoded active threshold is temporary until we have order statuses coming back
// on orders
const ActiveThreshold = 45; // minutes
export const isActive = (order: Order) => {
    const orderedAt = parse(order.order_timestamp, OrderTimestampParse, new Date());
    const elapsed = differenceInMinutes(new Date(), orderedAt);

    return elapsed <= ActiveThreshold;
};

// example: 05/16/2022 11:39:05
const OrderTimestampParse = 'LL/dd/yyyy HH:mm:ss';

export const isPickup = (order: Order) => {
    switch (order.delivery_type.toLowerCase()) {
        // because of course these are inconsistent
        case 'p':
        case 'pickup':
            return true;
        default:
            return false;
    }
};

export const isDelivery = (order: Order): order is DeliveryOrder => !isPickup(order);

export const toStorePoint = (order: Order): Point => {
    return {
        lat: order.store_lat,
        lng: order.store_lng
    };
};

export const toDeliveryPoint = (order: Order): Point | undefined => {
    if (isDelivery(order)) {
        return {
            lat: parseFloat(order.delivery_address_lat.toString()),
            lng: parseFloat(order.delivery_address_lng.toString())
        };
    }
};

export const getHistoryLabel = (order: Order, categories: MenuCategory[]) => {
    const firstItem = getFirstMainItem(order, categories);
    const additionalItems = order.items.length - 1;
    const { menu_item_name: menuItemName = 'Empty Order' } = firstItem ?? {};

    return additionalItems > 0
        ? `${menuItemName} + ${pluralize(additionalItems, 'item')}`
        : menuItemName;
};

export const getThumbnail = (order: Order, categories: MenuCategory[]) => {
    const firstItem = getFirstMainItem(order, categories);

    return firstItem?.image_url;
};

/**
 * Returns the first item in the order that is a meal like a salad or a bowl
 * will return drinks, etc. if there are no mains in the order
 **/
const getFirstMainItem = (order: Order, categories: MenuCategory[]) => {
    return (
        order.items.find((oi) => {
            const categoryId = parseInt(oi.category);
            return isPrimaryCategory(categories, categoryId);
        }) ?? order.items[0]
    );
};
/**
 * Returns three lines to be displayed on an order detail page. If the
 * order is pickup, the lines will be:
 *
 *    [store name, store address, phone store phone number]
 *
 * if the order is delivery, the lines will be:
 *
 *    [address line one, address line two, phone number]
 *
 **/
export const getOrderAddressLines = (order: Order): [string, string, string] => {
    if (isPickup(order)) {
        return [order.store_name, order.address_1, formatPhone(order.phone)];
    }

    // have to use assertions b/c order is not a discriminated union,
    // but these are always present on delivery orders.
    return [order.delivery_address_1!, order.delivery_address_2!, formatPhone(order.phone)];
};

export const getOrderTimestamp = (order: Order): Date => parseOrderTime(order.order_timestamp);

export const getPickupTimestamp = (order: Order): Date | undefined =>
    order.order_pickup_timestamp ? parseOrderTime(order.order_pickup_timestamp) : undefined;

export const parseOrderTime = (orderTime: string): Date =>
    parse(orderTime, OrderTimestampParse, new Date());

export const filterEmptyOrders = (orders: Order[] = []): Order[] =>
    orders.filter((order) => order.items.length > 0);
